import { useState } from "react";
import { Check, Minus, Plus } from "@phosphor-icons/react";
import { FAMILIES } from "@/lib/colorFamily";

const Group = ({ title, testId, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div
      className="border-b border-white/10 py-4 last:border-0 last:pb-0"
      data-testid={testId}
    >
      <button
        type="button"
        data-testid={`${testId}-toggle`}
        onClick={() => setOpen((v) => !v)}
        className="group flex w-full items-center justify-between text-left"
      >
        <span className="text-sm font-semibold tracking-tight text-[color:var(--s-ink)]">
          {title}
        </span>
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/[0.06] text-[color:var(--s-mute)] transition-colors duration-200 group-hover:bg-[color:var(--s-gold-bright)] group-hover:text-[#1a1a18]">
          {open ? (
            <Minus size={12} weight="bold" />
          ) : (
            <Plus size={12} weight="bold" />
          )}
        </span>
      </button>
      <div
        className={`grid transition-all duration-300 ${
          open
            ? "mt-3 grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
};

const Row = ({ label, count, on, onClick, testId, gold }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`group flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors duration-150 ${
      on ? "bg-white/[0.07]" : "hover:bg-white/[0.04]"
    }`}
  >
    <span
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-[3px] border transition-colors duration-200 ${
        on
          ? "border-[color:var(--s-gold-bright)] bg-[color:var(--s-gold-bright)]"
          : "border-[color:var(--s-line)] bg-transparent group-hover:border-[color:var(--s-gold-bright)]"
      }`}
    >
      {on && <Check size={12} weight="bold" className="text-[#1a1a18]" />}
    </span>
    <span
      className={`flex-1 text-sm transition-colors duration-200 ${
        gold
          ? "text-[color:var(--s-gold)]"
          : on
            ? "font-medium text-[color:var(--s-ink)]"
            : "text-[color:var(--s-mute)] group-hover:text-[color:var(--s-ink)]"
      }`}
    >
      {label}
    </span>
    <span className="rounded-full bg-white/[0.06] px-2 py-0.5 text-[11px] font-medium text-[color:var(--s-mute)]">
      {count}
    </span>
  </button>
);

const slug = (v) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const InventoryFilters = ({
  facets,
  picked,
  toggle,
  clear,
  activeCount,
}) => (
  <aside data-testid="inventory-filters" className="mt-8">
    <div className="flex items-center justify-between">
      <span className="text-base font-semibold tracking-tight text-[color:var(--s-ink)]">
        Refine
      </span>
      {activeCount > 0 && (
        <button
          type="button"
          data-testid="filters-clear"
          onClick={clear}
          className="rounded-full bg-white/[0.06] px-3 py-1 text-xs font-medium text-[color:var(--s-gold-bright)] transition-colors duration-200 hover:bg-[color:var(--s-gold-bright)] hover:text-[#1a1a18]"
        >
          Clear {activeCount}
        </button>
      )}
    </div>

    {facets.color.length > 0 && (
      <Group title="Color" testId="facet-color">
        <div className="flex flex-wrap gap-2.5">
          {facets.color.map(([value, count]) => {
            const swatch = (FAMILIES.find(([n]) => n === value) || [])[1];
            const on = picked.color.includes(value);
            return (
              <button
                key={value}
                type="button"
                data-testid={`facet-color-${value.toLowerCase()}`}
                title={`${value} (${count})`}
                onClick={() => toggle("color", value)}
                className={`flex h-9 w-9 items-center justify-center rounded-full shadow-[0_1px_2px_rgba(0,0,0,0.3)] transition-transform duration-200 hover:scale-110 ${
                  on
                    ? "ring-2 ring-[color:var(--s-gold-bright)] ring-offset-2 ring-offset-[color:var(--s-dark)]"
                    : "ring-1 ring-white/15"
                }`}
                style={{ background: swatch }}
              >
                {on && (
                  <Check
                    size={13}
                    weight="bold"
                    className={
                      ["White", "Beige", "Gold"].includes(value)
                        ? "text-[color:var(--s-ink)]"
                        : "text-white"
                    }
                  />
                )}
              </button>
            );
          })}
        </div>
      </Group>
    )}

    {facets.price?.length > 0 && (
      <Group title="Price" testId="facet-price">
        {facets.price.map((b) => (
          <Row
            key={b.id}
            label={b.label}
            count={b.count}
            testId={`facet-price-${b.id}`}
            on={picked.price?.includes(b.id)}
            onClick={() => toggle("price", b.id)}
          />
        ))}
      </Group>
    )}

    {[
      ["Material", "material"],
      ["Product type", "type"],
      ["Finish", "finish"],
      ["Brand", "brand"],
      ["Thickness", "thickness"],
    ].map(([title, field]) =>
      facets[field].length > 0 ? (
        <Group key={field} title={title} testId={`facet-${field}`}>
          {facets[field].map(([value, count]) => (
            <Row
              key={value}
              label={value}
              count={count}
              gold={value === "Remnant"}
              testId={`facet-${field}-${slug(value)}`}
              on={picked[field].includes(value)}
              onClick={() => toggle(field, value)}
            />
          ))}
        </Group>
      ) : null,
    )}
  </aside>
);
